import { CloseOutlined } from '@ant-design/icons';
import { useAccess } from '@umijs/max';
import type { FormInstance } from 'antd';
import { Alert, Button, Form, Modal, message, Space, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import type { DuplicateEntity, DuplicateResult, NameValues } from './search';
import { nameKey, nameTerms, searchDuplicateNames } from './search';

const EMPTY_RESULT: DuplicateResult = {
  items: [],
  truncated: false,
  failed: false,
};
function Matches({
  result,
  entity,
}: {
  result: DuplicateResult;
  entity: DuplicateEntity;
}) {
  return (
    <div>
      {result.items.map((item) => (
        <div key={item.uuid} style={{ marginTop: 4, overflowWrap: 'anywhere' }}>
          <Typography.Text strong>{item.name}</Typography.Text>
          <Typography.Text type="secondary">
            {` · ${item.exact ? '同名' : '相似名称'}${entity === 'lead' ? `（${item.matchedFields.join('、')}）` : ''}`}
            {item.ownerName ? ` · 负责人：${item.ownerName}` : ''}
            {item.customerName ? ` · 客户：${item.customerName}` : ''}
          </Typography.Text>
        </div>
      ))}
      {result.truncated && (
        <Typography.Text type="secondary">仅展示前 5 条</Typography.Text>
      )}
    </div>
  );
}

export function useDuplicateNameCheck({
  entity,
  form,
  enabled,
}: {
  entity: DuplicateEntity;
  form: FormInstance;
  enabled: boolean;
}) {
  const access = (useAccess() ?? {}) as Record<string, boolean>;
  const name = Form.useWatch('name', { form, preserve: true });
  const companyName = Form.useWatch('companyName', { form, preserve: true });
  const values = { name, companyName };
  const key = nameKey(entity, values);
  const accessKey = JSON.stringify(
    Object.entries(access)
      .filter(
        ([permission, allowed]) =>
          allowed &&
          /^(customers|contacts|leads|opportunities)-/.test(permission),
      )
      .map(([permission]) => permission)
      .sort(),
  );
  const [modal, contextHolder] = Modal.useModal();
  const [state, setState] = useState<{
    key: string;
    result: DuplicateResult;
    loading: boolean;
  }>();
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const revision = useRef(0);
  const locked = useRef(false);
  const alive = useRef(true);
  const latest = useRef({ enabled, key, accessKey });
  latest.current = { enabled, key, accessKey };
  const session = useRef(0);
  useEffect(() => {
    session.current += 1;
    return () => {
      session.current += 1;
    };
  }, [enabled]);
  const pending = useRef<
    | {
        destroy: () => void;
        cancel: () => void;
        key: string;
        accessKey: string;
      }
    | undefined
  >(undefined);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.cancel();
      pending.current?.destroy();
    };
  }, []);
  useEffect(() => {
    const current = ++revision.current;
    setState(undefined);
    const confirmation = pending.current;
    if (
      confirmation &&
      (!enabled ||
        confirmation.accessKey !== accessKey ||
        confirmation.key !== nameKey(entity, form.getFieldsValue(true)))
    ) {
      confirmation.cancel();
      confirmation.destroy();
    }
    const timer = setTimeout(async () => {
      if (!enabled || !nameTerms(entity, values).length) return;
      setState({ key, result: EMPTY_RESULT, loading: true });
      const result = await searchDuplicateNames(entity, values, access).catch(
        () => ({ ...EMPTY_RESULT, failed: true }),
      );
      if (alive.current && revision.current === current)
        setState({ key, result, loading: false });
    }, 400);
    return () => {
      clearTimeout(timer);
      revision.current += 1;
    };
  }, [enabled, entity, key, accessKey, retry]);

  async function submit<T>(
    submitted: NameValues,
    save: () => Promise<T>,
  ): Promise<T | false> {
    if (locked.current) return false;
    locked.current = true;
    setBusy(true);
    const snapshot = {
      enabled,
      key: nameKey(entity, submitted),
      accessKey,
      session: session.current,
    };
    const unchanged = () =>
      alive.current &&
      session.current === snapshot.session &&
      latest.current.enabled === snapshot.enabled &&
      latest.current.accessKey === snapshot.accessKey &&
      nameKey(entity, form.getFieldsValue(true)) === snapshot.key;
    try {
      if (enabled) {
        revision.current += 1;
        const result = await searchDuplicateNames(
          entity,
          submitted,
          access,
        ).catch(() => ({ ...EMPTY_RESULT, failed: true }));
        if (!unchanged()) return false;
        setState({ key: snapshot.key, result, loading: false });
        if (result.failed || result.items.length) {
          const confirmed = await new Promise<boolean>((resolve) => {
            const dialog = modal.confirm({
              title: result.items.length
                ? '已有同名或相似记录'
                : '暂时无法核验重复',
              content: (
                <Space
                  orientation="vertical"
                  style={{
                    maxHeight: 'max(80px, calc(100dvh - 240px))',
                    overflowY: 'auto',
                    width: '100%',
                    padding: 4,
                    margin: -4,
                  }}
                >
                  <Typography.Text>
                    请核对已有记录，确认仍需新增后继续。
                  </Typography.Text>
                  <Matches result={result} entity={entity} />
                  {result.failed && (
                    <Typography.Text type="warning">
                      暂时无法核验重复，检索结果可能不完整。
                    </Typography.Text>
                  )}
                </Space>
              ),
              centered: true,
              width: 'min(520px, calc(100vw - 48px))',
              okButtonProps: { style: { boxShadow: 'none', border: 'none' } },
              cancelButtonProps: { type: 'text' },
              okText: '继续新增',
              cancelText: '取消',
              closable: { 'aria-label': '关闭' },
              closeIcon: <CloseOutlined aria-label="关闭" />,
              keyboard: true,
              onOk: () => {
                resolve(true);
              },
              onCancel: () => {
                resolve(false);
              },
              afterClose: () => {
                resolve(false);
              },
            });
            pending.current = {
              key: snapshot.key,
              accessKey: snapshot.accessKey,
              destroy: dialog.destroy,
              cancel: () => resolve(false),
            };
          });
          pending.current = undefined;
          if (!confirmed) return false;
          if (!unchanged()) {
            message.info('名称已变化，请重新保存');
            return false;
          }
        }
      }
      return await save();
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  }

  const visible =
    enabled && nameTerms(entity, values).length > 0 && state?.key === key;
  const hint = (
    <>
      {contextHolder}
      {visible &&
        state &&
        (state.loading ? (
          <Typography.Text type="secondary" role="status">
            正在检索已有记录…
          </Typography.Text>
        ) : state.result.failed || state.result.items.length ? (
          <Alert
            type="warning"
            title={
              state.result.items.length
                ? '已有同名或相似记录，请核对'
                : '暂时无法核验重复'
            }
            description={
              <>
                <Matches result={state.result} entity={entity} />
                {state.result.failed && (
                  <div>
                    <Typography.Text type="warning">
                      暂时无法核验重复
                    </Typography.Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setRetry((value) => value + 1)}
                    >
                      重试
                    </Button>
                  </div>
                )}
              </>
            }
          />
        ) : null)}
    </>
  );
  return { hint, submit, busy };
}
